/* Native elements remain usable before JavaScript or when motion is disabled. */
(() => {
  class SweetHeader extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.menu = this.querySelector('.se-menu');
      this.summary = this.menu?.querySelector('summary');
      this.menuBackground = new Map();
      this.syncMenu = () => {
        const open = Boolean(this.menu?.open);
        document.body.classList.toggle('se-menu-open', open);
        this.summary?.setAttribute('aria-label', open ? 'Close navigation menu' : 'Open navigation menu');
        this.summary?.setAttribute('aria-expanded', String(open));
        if (open) {
          document.querySelectorAll('main, .sweet-footer, .rm-dock, .sb-mobile-dock, .se-cart-checkout-dock').forEach(node => {
            if (!this.menuBackground.has(node)) this.menuBackground.set(node, node.inert);
            node.inert = true;
          });
        } else {
          this.menuBackground.forEach((previous, node) => { node.inert = previous; });
          this.menuBackground.clear();
        }
      };
      this.menu?.addEventListener('toggle', this.syncMenu, { signal });
      this.syncMenu();
      this.addEventListener('keydown', event => {
        if (event.key === 'Tab' && this.menu?.open) {
          const items = [this.summary, ...this.menu.querySelectorAll('.se-shop-menu a[href]')];
          const first = items[0], last = items[items.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }
        if (event.key === 'Escape' && this.menu?.open) {
          this.menu.open = false;
          this.summary.focus();
        }
      }, { signal });
      this.addEventListener('click', event => {
        if (event.target.closest('.se-menu__panel a')) this.menu.open = false;
      }, { signal });
      document.addEventListener('click', event => {
        if (!this.contains(event.target) && this.menu?.open) this.menu.open = false;
      }, { signal });
      this.resize = new ResizeObserver(() => {
        document.body.style.setProperty('--header-height', `${this.offsetHeight}px`);
        document.body.style.setProperty('--header-group-height', `${this.offsetHeight}px`);
        if (window.innerWidth > 959 && this.menu?.open) this.menu.open = false;
      });
      this.resize.observe(this);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resize?.disconnect();
      document.body.classList.remove('se-menu-open');
      this.menuBackground?.forEach((previous, node) => { node.inert = previous; });
    }
  }

  class SweetHero extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.layers = Array.from(this.querySelectorAll('[data-depth]'));
      this.active = false;
      this.visible = true;
      this.pointerX = 0;
      this.pointerY = 0;
      this.frame = 0;
      this.baseRotations = new Map(this.layers.map(layer => [layer, layer.classList.contains('se-layer--belt') ? -16 : layer.classList.contains('se-layer--ring') ? -17 : layer.classList.contains('se-layer--foreground') ? 30 : 0]));
      // Motion is part of the art direction; reduced-motion preferences take priority.
      if (!this.reduced.matches && this.dataset.motionAllowed === 'true') this.classList.add('se-ready');
      this.applyPreference = () => {
        this.setMotion(!this.reduced.matches);
      };
      this.applyPreference();
      this.reduced.addEventListener('change', this.applyPreference, { signal });
      this.addEventListener('pointermove', event => {
        if (!this.active || !this.finePointer.matches) return;
        const bounds = this.getBoundingClientRect();
        this.pointerX = ((event.clientX - bounds.left) / bounds.width - .5) * 23;
        this.pointerY = ((event.clientY - bounds.top) / bounds.height - .5) * 18;
        this.requestFrame();
      }, { signal, passive: true });
      this.addEventListener('pointerleave', () => {
        this.pointerX = 0;
        this.pointerY = 0;
        this.requestFrame();
      }, { signal });
      // Horizon scrolls its page wrapper on desktop; capture covers both scroll roots.
      document.addEventListener('scroll', () => this.requestFrame(), { signal, passive: true, capture: true });
      document.addEventListener('visibilitychange', () => {
        this.dataset.visible = String(this.visible && !document.hidden);
        if (document.hidden) {
          cancelAnimationFrame(this.frame);
          this.frame = 0;
        }
        else this.requestFrame();
      }, { signal });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.dataset.visible = String(this.visible && !document.hidden);
        if (this.visible) this.requestFrame();
      }, { threshold: 0 });
      this.observer.observe(this);
    }
    setMotion(enabled) {
      this.active = Boolean(enabled && !this.reduced.matches && this.dataset.motionAllowed === 'true');
      this.dataset.motion = String(this.active);
      if (!this.active) {
        cancelAnimationFrame(this.frame);
        this.frame = 0;
        this.layers.forEach(layer => { layer.style.transform = ''; });
      } else {
        this.dataset.visible = String(this.visible && !document.hidden);
        this.requestFrame();
      }
    }
    requestFrame() {
      if (!this.active || !this.visible || this.reduced.matches || document.hidden || this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        const bounds = this.getBoundingClientRect();
        const progress = Math.max(0, Math.min(1, -bounds.top / Math.max(1, bounds.height)));
        this.layers.forEach(layer => {
          const depth = Number(layer.dataset.depth);
          const x = this.pointerX * depth;
          const y = this.pointerY * depth + progress * depth * 90;
          const rotation = this.baseRotations.get(layer) + progress * depth * 9;
          layer.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) rotate(${rotation.toFixed(2)}deg)`;
        });
      });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  }
  if (!customElements.get('sweet-header')) customElements.define('sweet-header', SweetHeader);
  if (!customElements.get('sweet-hero')) customElements.define('sweet-hero', SweetHero);
})();
